import { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import moment from "moment";
import { apiFetch } from "../lib/api";
import { toast } from "react-toastify";

type FileItem = {
  name: string;
  url: string;
  downloadUrl?: string;
  createdAt?: string;
  invoiceNo?: string | null;
  companyName?: string | null;
  total?: number | null;
};

export default function FilesPage() {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch("/api/files")
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) {
          setFiles(Array.isArray(data) ? data : []);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Unable to load files.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleDelete = async (name: string) => {
    try {
      const response = await apiFetch(`/api/files/${encodeURIComponent(name)}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete");
      }
      setFiles((prev) => prev.filter((file) => file.name !== name));
      toast.success("Document deleted.");
    } catch {
      setError("Unable to delete file.");
      toast.error("Unable to delete file.");
    }
  };

  return (
    <section className="grid gap-6">
      <div className="card p-6">
        <h2 className="font-display text-xl font-semibold text-ink">Documents</h2>
        <p className="text-sm text-slate-500">
          Generated invoice PDFs stored securely in object storage appear here.
        </p>
      </div>

      <div className="card p-6">
        {loading ? (
          <p className="text-sm text-slate-500">Loading files...</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : files.length === 0 ? (
          <p className="text-sm text-slate-500">No files found yet.</p>
        ) : (
          <ul className="grid gap-3">
            {files.map((file) => (
              <li
                key={file.name}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-ink">{file.name}</p>
                  {(file.invoiceNo || file.companyName || file.total !== null) && (
                    <p className="text-xs text-slate-500">
                      {file.invoiceNo ? `Invoice: ${file.invoiceNo}` : ""}{" "}
                      {file.companyName ? `· ${file.companyName}` : ""}
                      {file.total !== null ? ` · ₹ ${file.total?.toFixed(2)}` : ""}
                    </p>
                  )}
                  {file.createdAt && (
                    <p className="text-xs text-slate-400">
                      Generated: {moment(file.createdAt).format("ddd, h:mm A, D MMMM YYYY")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="button-secondary"
                  >
                    Open
                  </a>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => handleDelete(file.name)}
                    title="Delete file"
                  >
                    <FaTrash />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
